import React, {useEffect, useMemo, useRef, useState} from "react";
import styles from "./VariableBuyCostModal.module.css";
import {Modal, ModalBody, ModalFooter, ModalHeader, ModalProps} from "@/components/Modal/Modal";
import Button from "@/components/forms/Button/Button";
import Skeleton from "@/components/Skeleton/Skeleton";
import StockSection from "@/components/VariableBuyCost/StockSection";
import { Cost, Purchase } from "@/components/VariableBuyCost/constants/types";
import {useBuyCosts} from "@/components/VariableBuyCost/hooks/useBuyCosts";
import { BuyCostEntry } from "@boxem/core-api-schema";
import {Failure} from "@/components/VariableBuyCost/Failure";
import {Success} from "@/components/VariableBuyCost/Success";
import { useBuyCostContext } from "@/contexts/BuyCostContext";
import { Product } from "@/contexts/InventoryContext";
import { dayjsInUTC } from "@/utils/dateUtils";
import { productConditionToString } from "@/app/fba/utils";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import CopyToClipBoard from "@/components/CopyToClipboard/CopyToClipboard";

export interface VariableBuyCostModalProps extends ModalProps{
    inventoryItem: Product;
    onUpdate?: () => void;
}

function generateTimestampId(prefix = 'id') {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export default function VariableBuyCostModal(props: VariableBuyCostModalProps) {
    const { buyCosts, loading, error, fetchBuyCosts, updateBuyCosts, updateSuccess } = useBuyCosts(props.inventoryItem.sellerData[0].productId);
    const [editedBuyCosts, setEditedBuyCosts] = useState<Purchase[]>([]);
    const datePickerRefs = useRef<{ [key: string]: any }>({});

    // Sort purchases by date
    const sortPurchasesByDate = (purchases: Purchase[]): Purchase[] => {
        return [...purchases].sort((a, b) => {
            const dateA = !a.date || a.date.trim() === '' ? dayjsInUTC('1900-01-01') : dayjsInUTC(a.date);
            const dateB = !b.date || b.date.trim() === '' ? dayjsInUTC('1900-01-01') : dayjsInUTC(b.date);
            return dateA.valueOf() - dateB.valueOf();
        }).map((purchase, index) => ({
            ...purchase,
            title: index === 0 ? "Initial Purchase" : `Restock ${index}`,
            isInitial: index === 0
        }));
    };

    const getAllUsedDates = useMemo(() => {
        return editedBuyCosts
            .filter(purchase => purchase.date)
            .map(purchase => purchase.date);
    }, [editedBuyCosts]);


    // Get the latest purchase date from all purchases
    const getLatestPurchaseDate = (): string | undefined => {
        if (editedBuyCosts.length === 0) return undefined;

        return [...editedBuyCosts] // Create a copy before sorting
            .sort((a, b) => {
                const dateA = !a.date || a.date.trim() === '' ? dayjsInUTC('1900-01-01') : dayjsInUTC(a.date);
                const dateB = !b.date || b.date.trim() === '' ? dayjsInUTC('1900-01-01') : dayjsInUTC(b.date);
                return dateB.valueOf() - dateA.valueOf();
            })[0].date;
    };

    useEffect(() => {
        fetchBuyCosts();
    }, [fetchBuyCosts]);

    useEffect(() => {
        if (buyCosts && buyCosts.length > 0) {
            setEditedBuyCosts(buyCosts.map((buyCost, index) => ({
                id: generateTimestampId(),
                title: index === 0 ? "Initial Purchase" : `Restock ${index}`,
                date: buyCost.datePurchased ? dayjsInUTC(buyCost.datePurchased.toString()).format('YYYY-MM-DD') : undefined,
                costs: buyCost.costCategories?.length > 0 ? buyCost.costCategories.map(cost => {
                    return {
                        category: cost.costCategoryName,
                        description: cost.description,
                        cost: cost.total,
                        costString: cost.total.toFixed(2),
                    };
                }) : [{
                    category: "Buy Cost",
                    description: "",
                    cost: 0,
                    costString: "0.00"
                }],
                isInitial: index === 0,
            })));
        }
    }, [buyCosts]);

    const getDefaultDate = (): string => {
        const today = dayjsInUTC().format('YYYY-MM-DD');
        const latestDate = getLatestPurchaseDate();

        if (!latestDate) {
            return today; // If no previous purchases, use today
        }

        // If today is after the latest date, use today
        // Otherwise, use the day after the latest date
        return dayjsInUTC(today).isAfter(latestDate)
            ? today
            : dayjsInUTC(latestDate).add(1, 'day').format('YYYY-MM-DD');
    };

    const addPurchase = () => {
        setEditedBuyCosts(prev => [
            ...prev,
            {
                id: generateTimestampId(),
                title: `Restock ${prev.length}`,
                date: getDefaultDate(),
                costs: [{ category: "Buy Cost", description: "", cost: 0, costString: "0.00" }]
            }
        ]);
    };

    const updatePurchaseDate = (id: string, index: number, field: keyof Purchase, value: string) => {
        const newPurchases = [...editedBuyCosts];
        newPurchases[index][field] = value as never; // Type as

        const sorted = sortPurchasesByDate(newPurchases);

        setEditedBuyCosts(sorted);

        requestAnimationFrame(() => {
            const datePickerRef = datePickerRefs.current[id];
            if (datePickerRef) {
                datePickerRef.focus();
                datePickerRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        });
    }

    const addCostToPurchase = (purchaseIndex: number) => {
        const newPurchases = [...editedBuyCosts];
        newPurchases[purchaseIndex].costs.push({ category: "Buy Cost", description: "", cost: 0 });
        setEditedBuyCosts(newPurchases);
    };

    const deleteCostFromPurchase = (purchaseIndex: number, costIndex: number) => {
        const newPurchases = [...editedBuyCosts];
        newPurchases[purchaseIndex].costs.splice(costIndex, 1);
        setEditedBuyCosts(newPurchases);
    };

    const updateCostInPurchase = (purchaseIndex: number, costIndex: number, field: keyof Cost, value: string | number) => {
        const newPurchases = [...editedBuyCosts];
        newPurchases[purchaseIndex].costs[costIndex][field] = value as never; // Type assertion needed due to union type
        setEditedBuyCosts(newPurchases);
    };

    const deletePurchaseSection = (purchaseIndex: number) => {
        const newPurchases = [...editedBuyCosts];
        newPurchases.splice(purchaseIndex, 1);
        setEditedBuyCosts(newPurchases);
    };

    const handleSave = async () => {
        const buyCostToUpdate: BuyCostEntry[] = editedBuyCosts.map((editedBuyCosts, index) => {
            return {
                //maybe should calculate total at the backend
                total: editedBuyCosts.costs.reduce((acc, cost) => acc + cost.cost, 0),
                datePurchased: dayjsInUTC(editedBuyCosts.date).format('YYYY-MM-DD'),
                costCategories: editedBuyCosts.costs?.length ? editedBuyCosts.costs.map(cost => ({
                    costCategoryName: cost.category,
                    description: cost.description,
                    total: cost.cost
                })) : [{
                    costCategoryName: "Buy Cost",
                    description: "",
                    total: 0
                }]
            };
        });
        await updateBuyCosts(buyCostToUpdate);
        props.onUpdate?.();
    }

    const { inventoryItem} = props;

    return (
        <Modal
            {...props}
            className={styles.modal}
            closeIcon
        >
            <ModalHeader>
                <div className={styles.header}>
                    {inventoryItem.image ? (
                        <img
                            src={inventoryItem.image}
                            width="64"
                            height="64"
                            alt={inventoryItem.name}
                            className={styles.inventoryImage}
                        />
                    ): (
                        <div className={styles.skeletonContainer}>
                            <Skeleton type="image" disableShimmer width={64} height={64} />
                        </div>
                    )}
                    <div className={styles.metaDataContainer}>
                        <span className={styles.metaDataTitle}>
                            {inventoryItem.name}
                        </span>
                        <div className={styles.metaDataSubContainer}>
                            <div className={styles.skuAndCondition}>
                                <CopyToClipBoard value={inventoryItem.sellerData[0].sku} tooltipText="Copy SKU">
                                    <span className={styles.subTitleContainer}>

                                        <span className={styles.subMetaTitle}>
                                            SKU:
                                        </span>
                                        <span className={styles.subMetaValue}>
                                            {inventoryItem.sellerData[0].sku}
                                        </span>
                                    </span>
                                </CopyToClipBoard>
                                <span className={styles.dot}>
                                    •
                                </span>
                                <span className={styles.subTitleContainer}>
                                    <span className={styles.subMetaTitle}>
                                        Condition:
                                    </span>
                                    <span className={styles.subMetaValue}>
                                        {productConditionToString(inventoryItem.sellerData[0].condition)}
                                    </span>
                                </span>
                                <StatusBadge
                                    status={inventoryItem.sellerData[0].isFBM ? "danger" : "warning"}
                                    label={inventoryItem.sellerData[0].isFBM ? "FBM" : "FBA"}
                                    className={styles.statusBadge}
                                />
                            </div>
                            <CopyToClipBoard value={inventoryItem.asin} tooltipText="Copy ASIN">
                                <span className={styles.subTitleContainer}>
                                    <span className={styles.subMetaTitle}>
                                        ASIN:
                                    </span>
                                    <span className={styles.subMetaValue}>
                                        {inventoryItem.asin}
                                    </span>
                                </span>
                            </CopyToClipBoard>
                        </div>
                    </div>
                </div>
            </ModalHeader>
            <ModalBody className={styles.modalBody}>
                {loading && (
                    <div>
                        <LoadingSpinner size="3rem" />
                    </div>
                )}
                {error && (<Failure errorMessage={error}/>)}
                {!loading && !error && updateSuccess && (<Success successMessage="Successfully updated your costs data!"/>)}
                {!loading && !error && !updateSuccess && <div className={styles.stockSectionsContainer}>
                    {editedBuyCosts.map((purchase, index) => (
                        <StockSection
                            ref={el => datePickerRefs.current[purchase.id] = el}
                            key={index}
                            {...purchase}
                            onAddCost={() => addCostToPurchase(index)}
                            onDeleteCost={(costIndex) => deleteCostFromPurchase(index, costIndex)}
                            onChangeCost={(costIndex, field, value) => updateCostInPurchase(index, costIndex, field, value)}
                            onChangeDate={(date) => updatePurchaseDate(purchase.id, index, 'date', date)}
                            deletable={!purchase.isInitial}
                            onDeleteSection={() => deletePurchaseSection(index)}
                            latestPurchaseDate={index === 0 ? undefined : getLatestPurchaseDate()}
                            isInitialPurchase={index === 0}
                            usedDates={getAllUsedDates}
                        />
                    ))}
                </div>}
            </ModalBody>
            {!(loading || !!error || updateSuccess) && <ModalFooter className={styles.modalFooter}>
                <div className={styles.buttonContainer}>
                    <Button type={"primary"} onClick={addPurchase} disabled={(loading || !!error || updateSuccess)}>
                        Add Restock
                    </Button>
                    <div className={styles.cancelSaveButtons}>
                        <Button type={"secondary"} onClick={props.onClose} disabled={(loading || !!error)}>
                            Cancel
                        </Button>
                        <Button type={"primary"} onClick={handleSave} disabled={(loading || !!error || updateSuccess)}>
                            Save
                        </Button>
                    </div>
                </div>
            </ModalFooter>}
        </Modal>
    );
}